import { Link, useParams } from "react-router-dom";
import { Info, ArrowRight } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { PageHero, ClosingCta, RequestButton, FaqList, NumberedList } from "../components/site/Blocks";
import { Reveal, SectionHeading } from "../components/site/Primitives";
import { SERVICES, getService } from "../data/services";
import { ARTICLES } from "../data/insights";
import NotFound from "./NotFound";

const FACILITATION = [
  { t: "Requirement review", d: "We review your brief and clarify scope, jurisdiction, timing and any regulatory or independence considerations." },
  { t: "Suitability assessment", d: "Potential professionals are assessed against relevant experience, qualifications and availability." },
  { t: "Conflicts and confidentiality", d: "Professionals are asked to disclose known conflicts, and confidentiality arrangements are agreed before sensitive information is shared." },
  { t: "Shortlist and introduction", d: "You review a focused shortlist and choose whom to meet. Engagement terms are then agreed between the parties." },
];

export default function ServicePage() {
  const { slug } = useParams();
  const s = getService(slug);
  if (!s) return <NotFound />;
  const related = ARTICLES.filter((a) => a.service === s.slug);
  const others = SERVICES.filter((o) => o.slug !== s.slug);
  const faqLd = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: s.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };

  return (
    <>
      <Seo title={`Independent ${s.name} Specialists`} description={`${s.summary} Explore independent ${s.name.toLowerCase()} expertise available through the Trusted Leap network.`} path={`/services/${s.slug}`} jsonLd={faqLd} />
      <PageHero eyebrow={`Services · ${s.name}`} title={s.heroTitle} intro={s.heroIntro} image={s.image} imageAlt={s.imageAlt}>
        <RequestButton category={s.name} testId="service-hero-request-button" />
      </PageHero>

      <section className="bg-white py-24 lg:py-28">
        <div className="container-x grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-4"><p className="eyebrow">Overview</p><h2 className="h-section mt-5">{s.name} through the network</h2></Reveal>
          <Reveal delay={0.1} className="prose-tl text-lg lg:col-span-7 lg:col-start-6">
            {s.overview.map((p) => <p key={p.slice(0, 20)}>{p}</p>)}
            <div className="mt-10 flex gap-4 rounded-lg border border-blue-100 bg-blue-50/60 p-6" data-testid="service-regulatory-note">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#2563EB]" strokeWidth={1.6} />
              <p className="!mb-0 text-sm leading-relaxed !text-[#1E3A8A]">{s.regulatory}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F4F6F8] py-24 lg:py-28">
        <div className="container-x">
          <SectionHeading eyebrow="Challenges addressed" title="Where organisations typically seek support." />
          <NumberedList items={s.challenges} className="mt-14" />
        </div>
      </section>

      <section className="bg-white py-24 lg:py-28">
        <div className="container-x grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Expertise available" title="Relevant professional backgrounds." intro="Types of independent practitioners who may be considered, subject to availability and suitability for the engagement." />
          </div>
          <ul className="lg:col-span-6 lg:col-start-7">
            {s.expertise.map((e) => (
              <li key={e} className="flex items-center gap-5 border-b border-slate-200 py-5 text-lg text-[#0B1220] first:border-t">
                <span className="h-px w-6 bg-[#2563EB]" /> {e}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-[#0B1220] py-24 text-white lg:py-28">
        <div className="container-x">
          <SectionHeading dark eyebrow="Typical scenarios" title="Illustrative engagement scenarios." intro="Generic examples to illustrate the kinds of requests the network is designed to support. They do not describe specific client engagements." />
          <div className="mt-14 grid gap-px overflow-hidden rounded-lg border border-white/10 bg-white/10 md:grid-cols-2">
            {s.scenarios.map((sc) => (
              <div key={sc.title} className="bg-[#0B1220] p-8 lg:p-10">
                <h3 className="font-display text-xl font-semibold">{sc.title}</h3>
                <p className="mt-3 leading-relaxed text-slate-400">{sc.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-28">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <SectionHeading eyebrow="How we facilitate matching" title="A curated, transparent matching process." />
            <Link to="/how-it-works" className="link-arrow" data-testid="service-how-it-works-link">How It Works <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {FACILITATION.map((f, i) => (
              <Reveal key={f.t} delay={i * 0.06} className="border-t-2 border-[#0B1220] pt-6">
                <span className="text-xs font-semibold tracking-[0.2em] text-slate-400">0{i + 1}</span>
                <h3 className="mt-3 font-display text-lg font-semibold text-[#0B1220]">{f.t}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{f.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white py-24 lg:py-28">
        <div className="container-x grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="FAQ" title={`${s.name} questions`} />
            <div className="mt-10"><RequestButton category={s.name} testId="service-faq-request-button" /></div>
          </div>
          <div className="lg:col-span-7 lg:col-start-6"><FaqList faqs={s.faqs} prefix={s.slug} /></div>
        </div>
      </section>

      <section className="bg-[#F4F6F8] py-20">
        <div className="container-x grid gap-12 lg:grid-cols-2">
          <div>
            <p className="eyebrow">Other disciplines</p>
            <ul className="mt-6 flex flex-wrap gap-3">
              {others.map((o) => (
                <li key={o.slug}><Link to={`/services/${o.slug}`} className="inline-flex rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-[#0B1220] transition-colors hover:border-[#2563EB] hover:text-[#2563EB]" data-testid={`service-other-${o.slug}`}>{o.name}</Link></li>
              ))}
            </ul>
          </div>
          {related.length > 0 && (
            <div>
              <p className="eyebrow">Related insights</p>
              <ul className="mt-6 space-y-3">
                {related.map((a) => (
                  <li key={a.slug}><Link to={`/insights/${a.slug}`} className="link-arrow" data-testid={`service-related-${a.slug}`}>{a.title} <ArrowRight className="h-4 w-4" /></Link></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
      <ClosingCta />
    </>
  );
}
